/**
 * Display helpers for the extra order info: stop labels, receiver contacts,
 * order type, pickup time, goods and loaders. Shared by the booking panel,
 * TrackOrder and OrderHistory so the three say the same thing.
 *
 * Where the values come from:
 * - order_type, pickup_mode, scheduled_time, goods_description and
 *   loaders_count: order_details on /track and GET /orders?order_number, and
 *   the company list rows;
 * - stop_details: only on the signed-in GET /orders?order_number response
 *   (never on /track or /trip), as [{stop_index, contact_name,
 *   contact_phone, reference, instructions, redacted, ...}].
 *
 * Pure: no Vue, no axios.
 */
import { formatWallClock } from "./orderTime";

export interface StopDetailView {
  stop_index: number;
  contact_name?: string | null;
  contact_phone?: string | null;
  reference?: string | null;
  instructions?: string | null;
  redacted?: boolean;
}

/** "Pickup", "Drop-off", or "Drop-off 2" when there is more than one. */
export function stopLabel(index: number, stopCount: number): string {
  if (index === 0) return "Pickup";
  return stopCount > 2 ? `Drop-off ${index}` : "Drop-off";
}

/** The place name before the first ";" (names are "Place; street, area"). */
export function shortPlaceName(name: unknown): string {
  const s = typeof name === "string" ? name : "";
  return (s.split(";")[0] || "").trim() || s.trim();
}

/** "+254722000111" → "0722 000 111"; any other number is shown as stored. */
export function formatPhone(phone: unknown): string {
  const s = typeof phone === "string" ? phone.trim() : "";
  const m = /^\+254(\d{3})(\d{3})(\d{3})$/.exec(s);
  return m ? `0${m[1]} ${m[2]} ${m[3]}` : s;
}

/** href for a phone link, digits and a leading + only. */
export function telHref(phone: unknown): string {
  const s = typeof phone === "string" ? phone.replace(/[^\d+]/g, "") : "";
  return s ? `tel:${s}` : "";
}

/** stop_details as a map by stop index; bad entries are skipped. */
export function stopDetailsByIndex(list: unknown): Map<number, StopDetailView> {
  const out = new Map<number, StopDetailView>();
  if (!Array.isArray(list)) return out;
  for (const row of list) {
    const i = Number((row as any)?.stop_index);
    if (Number.isInteger(i) && i >= 0) out.set(i, row as StopDetailView);
  }
  return out;
}

export function hasVisibleStopDetail(d: StopDetailView | undefined | null): boolean {
  return !!(d && (d.contact_name || d.contact_phone || d.reference || d.instructions));
}

export function orderTypeLabel(type: unknown): string {
  if (type === "dedicated") return "Dedicated";
  if (type === "on_demand") return "On-Demand";
  return "";
}

/**
 * "As soon as possible" or the scheduled Nairobi time. "" for orders placed
 * before pickup_mode existed.
 */
export function pickupLabel(mode: unknown, scheduledTime: unknown): string {
  if (mode === "asap") return "As soon as possible";
  if (mode === "scheduled") return formatWallClock(scheduledTime) || "Scheduled";
  return "";
}

/** The CSV's plain value for the pickup mode. */
export function pickupModeLabel(mode: unknown): string {
  if (mode === "asap") return "ASAP";
  if (mode === "scheduled") return "Scheduled";
  return "";
}

/** "2 loaders"; "" when none were booked. */
export function loadersLabel(count: unknown): string {
  const n = Number(count);
  if (!Number.isInteger(n) || n <= 0) return "";
  return `${n} loader${n === 1 ? "" : "s"}`;
}

/**
 * TrackOrder's line about the driver the customer asked for, from
 * order_details.preferred ({status, until_utc}; no driver id or name, since
 * /track is public). "" once a driver is assigned, when none was asked for,
 * and once the 25 s window has passed (by then the order has gone to nearby
 * drivers too, so "offered to your chosen driver first" would mislead).
 */
export function preferredDriverNote(preferred: unknown, pendingWithoutDriver: boolean, now: number = Date.now()): string {
  if (!pendingWithoutDriver) return "";
  const status = (preferred as any)?.status;
  if (status === "requested") {
    const until = Date.parse(String((preferred as any)?.until_utc ?? ""));
    return Number.isFinite(until) && now < until ? "Offered to your chosen driver first." : "";
  }
  if (status === "unavailable") return "We couldn't reserve this driver, so your order went to nearby drivers.";
  return "";
}

/** Drop-off references, in stop order, joined like the drop locations. */
export function dropReferences(stopDetails: unknown, fallback: unknown): string {
  const map = stopDetailsByIndex(stopDetails);
  if (map.size) {
    return [...map.entries()]
      .filter(([i, d]) => i > 0 && d.reference)
      .sort((a, b) => a[0] - b[0])
      .map(([, d]) => String(d.reference).trim())
      .filter(Boolean)
      .join(" -- ");
  }
  if (Array.isArray(fallback)) return fallback.filter(Boolean).join(" -- ");
  return typeof fallback === "string" ? fallback : "";
}
